
import * as AppCommands from "@/assets/scripts/Application/Commands";
import * as PageCommands from "@/assets/scripts/PageEditor/Commands";
// Dependencies
import { clamp } from "@/assets/scripts/Utilities";
import { PointerTracker } from "@/assets/scripts/Utilities/PointerTracker";
import { TableColumnState, TabularProperty } from "@/assets/scripts/Page/Property";
import { defineComponent, markRaw, PropType, ref } from "vue";
// Components
import Trash from "@/components/Icons/Trash.vue";
import MoveDots from "@/components/Icons/MoveDots.vue";
import MoveArrow from "@/components/Icons/MoveArrow.vue";
import Duplicate from "@/components/Icons/Duplicate.vue";

export default defineComponent({
  name: "TabularField",
  setup() {
    return { 
      table: ref<HTMLElement | null>(null)
    };
  },
  props: {
    property: {
      type: Object as PropType<TabularProperty>,
      required: true
    }
  },
  data() {
    return {
      pointer: markRaw(new PointerTracker()),
      onResizeObserver: null as ResizeObserver | null
    }
  },
  computed: {

    /**
     * Returns the table's columns.
     * @returns
     *  The table's columns.
     */
    columns(): ReadonlyArray<TableColumnState> {
      return this.property.columnState;
    }

  },
  emits: ["execute"],
  methods: {

    /**
     * Row create behavior.
     */
    onCreate() {
      let cmd = PageCommands.createTabularPropertyRow(this.property);
      this.$emit("execute", cmd);
    },

    /**
     * Row duplicate behavior.
     * @param id
     *  The row's id.
     */
    onDuplicate(id: string) {
      let cmd = PageCommands.duplicateTabularPropertyRow(this.property, id);
      this.$emit("execute", cmd);
    },

    /**
     * Row delete behavior.
     * @param id
     *  The row's id.
     */
    onDelete(id: string) {
      let cmd = PageCommands.deleteTabularPropertyRow(this.property, id);
      this.$emit("execute", cmd);
    },

    /**
     * Row action behavior.
     * @param id
     *  The action's id. 
     */
    onAction(id: string) {
      let cmd = AppCommands.invokePropertyAction(this.property, id);
      this.$emit("execute", cmd);
    },

    /**
     * Row start drag behavior.
     * @param id
     *  The row's id.
     * @param event
     *  The pointer event.
     */
    onStartDrag(id: string, event: PointerEvent) {
      let row = (event.target! as HTMLElement).closest("tr")!;
      // Configure row classes
      for(let el of this.table!.children) {
        if(el === row) {
          el.classList.add("grabbed");
        } else {
          el.classList.add("not-grabbed");
        }
      }
      // Resolve row bounds
      let rows: { el: Element, minY: number, maxY: number }[] = [];
      for(let row of this.table!.children) {
        let bounds = row.getBoundingClientRect();
        rows.push({ 
          el: row,
          minY: bounds.y,
          maxY: bounds.y + bounds.height
        });
      }
      // Resolve row index
      let src = [...this.table!.children].indexOf(row);
      // Resolve row min and max
      let min = rows.at(0)!.minY - rows[src].minY;
      let max = rows.at(-1)!.maxY - rows[src].maxY;
      // Capture pointer
      let state = { id, src, dst: src, min, max, rows };
      this.pointer.capture(
        event, 
        (_, track) => {
          this.onDrag(track, state);
        }, 
        (_, _track) => {
          this.onStopDrag(state);
        });
    },

    /**
     * Row drag behavior.
     * @param track
     *  The mouse tracker.
     * @param state
     *  The drag state.
     */
    onDrag(track: PointerTracker, state: any) {
      let { src, min, max, rows } = state;
      let row = rows[src].el;
      // Determine row position
      let y = clamp(track.deltaY, min, max);
      // Apply row position
      row.style.transform = `translate(0px, ${ y }px)`;
      // Swap rows
      let bounds = row.getBoundingClientRect();
      let minY = bounds.y;
      let maxY = minY + bounds.height;
      let height = (maxY - minY) + 8;
      // Configure dst index
      state.dst = state.src;
      // Swap rows above index
      for(let i = 0, s = false; i < src; i++) {
        let r = rows[i];
        if(!s && minY <= r.maxY && maxY >= r.minY) {
          state.dst = i;
          s = true;
        }
        let tx = s ? `translate(0px, ${ height }px)` : "";
        r.el.style.transform = tx;
      }
      // Swap rows below index
      for(let i = rows.length - 1, s = false; i > src; i--) {
        let r = rows[i];
        if(!s && minY <= r.maxY && maxY >= r.minY) {
          state.dst = i;
          s = true;
        }
        let tx = s ? `translate(0px, -${ height }px)` : "";
        r.el.style.transform = tx;
      }
    },

    /**
     * Row stop drag behavior.
     * @param state
     *  The drag state.
     */
    onStopDrag(state: any) {
      let { id, src, dst, rows } = state;
      // Clear row css
      for(let r of rows) {
        r.el.classList.remove("grabbed", "not-grabbed");
        r.el.style.transform = "";
      }
      // If no movement
      if(src === dst) {
        return;
      }
      // If movement
      let cmd = PageCommands.moveTabularPropertyRow(this.property, id, dst);
      this.$emit("execute", cmd);
    }

  },
  mounted() {
    // Execute mount command
    let cmd = AppCommands.mountProperty(this.property, this.$el);
    this.$emit("execute", cmd);
  },
  unmounted() {
    // Execute destroy command
    let cmd = AppCommands.destroyProperty(this.property);
    this.$emit("execute", cmd);
  },
  components: { Trash, MoveDots, MoveArrow, Duplicate }
});
